import { useTranslation } from 'react-i18next';

import { useUiSettings } from '../features/config/core/UiSettingsContext';
import { useVersionUpdate } from '../features/version/core/VersionUpdateContext';
import { useBackendVersion } from '../hooks/api/useBackendVersion';
import { formatTimestamp } from '../utils/datetime';

/* injected by the webpack DefinePlugin at build time; not defined
   under jest, hence the typeof guards */
const COMMIT = typeof __GIT_COMMIT__ === 'undefined' ? 'dev' : __GIT_COMMIT__;
const BUILT = typeof __BUILD_TIME__ === 'undefined' ? '' : __BUILD_TIME__;

/* the frontend is stamped with the short sha; the backend answers with
   the full one, so it is cut to the same length — a footer is no place
   for forty hex digits, and seven of them identify a commit */
const SHORT_COMMIT_LENGTH = 7;

/* Stays next to the version for as long as the tab runs an outdated
   release — so that dismissing the hint postpones it rather than
   losing it. */
function UpdateMarker() {
  const { t } = useTranslation();
  const { updateAvailable, dismissed, reveal } = useVersionUpdate();

  if (!updateAvailable || !dismissed) return null;

  return (
    <button
      className="btn-update"
      title={t('version.markerTitle')}
      aria-label={t('version.markerTitle')}
      onClick={reveal}
    />
  );
}

/*
 * One build, named: "Frontend: 0f2ed02 (25.07.2026, 08:27:24)".
 *
 * Which half is which used to go without saying, because there was
 * only one. Now that the backend stands beside it, both carry their
 * name — an unlabelled pair of commits says nothing about which
 * belongs to what.
 */
type BuildProps = {
  name: string;
  commit: string | null | undefined;
  built: string | null | undefined;
  title?: string;
};

function Build({ name, commit, built, title }: BuildProps) {
  const { i18n } = useTranslation();

  if (!commit) return null;

  /* the stamp travels as UTC and becomes the local time of the reader.
     The dev marker "Hot Reload" is no timestamp and stays as it is. */
  const when = built ? formatTimestamp(built, i18n.resolvedLanguage) : '';

  return (
    <span className="build-version" title={title}>
      {name}:&nbsp;{commit}
      {when && ` (${when})`}
    </span>
  );
}

/*
 * Asks the backend again what it is running.
 *
 * The answer is cached for the whole session (`staleTime: Infinity`),
 * which is right for a footer — but it turns the line into a claim
 * about a moment that may be hours old, and a backend deployed since
 * then is exactly what the reader of a bug report needs to know
 * about. So the line says when it was asked, and this button asks
 * again, rather than the query polling a server that hardly ever
 * changes.
 */
function RefreshButton({
  onRefresh,
  busy,
}: {
  onRefresh: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();

  return (
    <button
      className="btn-version-refresh"
      title={t('version.refresh')}
      aria-label={t('version.refresh')}
      disabled={busy}
      onClick={onRefresh}
    />
  );
}

/*
 * Which builds are running — a question a bug report has to answer and
 * nobody else asks, so the stamps are off until the configuration
 * turns them on. What stays visible either way is the update marker:
 * it is not a version, it is the way back to a hint the reader
 * postponed, and hiding that would lose it rather than postpone it.
 */
export function Version() {
  const { t, i18n } = useTranslation();
  const { showVersions } = useUiSettings();
  /* an unreachable or older backend simply leaves its half out; the
     footer must not depend on the server answering — and while the
     stamps are hidden it is not asked at all */
  const {
    data: backend,
    dataUpdatedAt,
    isFetching,
    refetch,
  } = useBackendVersion({ enabled: showVersions });

  /* one box around both builds: the footer splits its two halves with
     space-between, and loose spans would be spread across it */
  return (
    <span className="footer-builds">
      {/* the frontend names itself out of a constant compiled into the
          bundle that is running, so it cannot be out of date */}
      {showVersions && (
        <Build name={t('version.frontend')} commit={COMMIT} built={BUILT} />
      )}
      {showVersions && backend?.gitCommit && (
        <span className="build-backend">
          <Build
            name={t('version.backend')}
            commit={backend.gitCommit.slice(0, SHORT_COMMIT_LENGTH)}
            built={backend.buildTime}
            title={t('version.backendAsked', {
              when: formatTimestamp(dataUpdatedAt, i18n.resolvedLanguage),
            })}
          />
          <RefreshButton onRefresh={() => refetch()} busy={isFetching} />
        </span>
      )}
      <UpdateMarker />
    </span>
  );
}
